'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { trackResonance, getUserResonanceSummary, updateUserProfile, ResonanceSummary } from '@/lib/bookmarks';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface LayerModule {
  icon: string;
  title: string;
  description: string;
  href: string;
}

interface ProfileLayer0 {
  entryStatements: string[];
  entryPrompts: string[];
  primaryModule: { title: string; href: string };
}

interface ProfileLayer1 {
  modules: LayerModule[];
  sequence: string;
  keysConnection: string;
}

interface ProfileLayer2 {
  deepConnections: string[];
  namesLink: { names: string[]; href: string };
  openLetterSection: string;
  reflection: string;
}

interface ProfileLayer3 {
  amanahTemplate: string;
}

interface CaretakerProfile {
  id: string;
  label: string;
  arabic: string;
  icon: string;
  color: string;
  zahir: string;
  layer0: ProfileLayer0;
  layer1: ProfileLayer1;
  layer2: ProfileLayer2;
  layer3: ProfileLayer3;
}

// ─── PROFILE DATA ─────────────────────────────────────────────────────────────

const CARETAKER_PROFILES: CaretakerProfile[] = [
  {
    id: 'inner-seeker',
    label: 'Inner Seeker',
    arabic: 'طَالِب الحَقِيقَة',
    icon: '◎',
    color: '#8a8ab8',
    zahir: 'Curious about the deeper meaning of what you see around you',
    layer0: {
      entryStatements: [
        'Curious about the deeper meaning of what you see around you',
        'Interested in the Names and Attributes of Allah',
        'Looking to connect your inner life to the world around you',
      ],
      entryPrompts: [
        'What in creation has stopped you lately?',
        'What does reflection mean to you?',
        'What connects your inner state to the world outside?',
      ],
      primaryModule: { title: 'IRL Lens', href: '/inner-reality-lens' },
    },
    layer1: {
      modules: [
        { icon: '◎', title: 'IRL Lens', description: 'Eight-layer recognition pathway from Mata to Uboor', href: '/inner-reality-lens' },
        { icon: '✦', title: 'Names Reference', description: '99+ Names — the vocabulary of recognition and reality', href: '/names-reference' },
        { icon: '✦', title: 'Open Letter', description: 'The foundation document — 32 sections of sacred language', href: '/open-letter' },
        { icon: '⌘', title: 'Keys (AYAT + HAAL)', description: 'The two foundational keys for the inner seeker', href: '/keys-to-kulli-haal' },
        { icon: '◈', title: 'SL Glossary', description: 'Sacred Language terms — Zahir, Batin, Wujud, and more', href: '/sl-glossary' },
      ],
      sequence: 'Leaf Sequence (Al-Qadir → Al-Khaliq)',
      keysConnection: 'Key 01: AYAT — Signs in creation as the primary language of recognition',
    },
    layer2: {
      deepConnections: [
        'Al-Batin — the hidden layer of every encounter you\'ve had',
        'Wujud — the is-ness that preceded your observation of any thing',
        'Basirah — insight paired with basar, seeing beyond the surface',
      ],
      namesLink: { names: ['Al-Batin', 'Al-Zahir', 'Al-Basir'], href: '/names-reference' },
      openLetterSection: 'Hawas, Examination, Wujud·Huduth',
      reflection: 'What did you observe today that you couldn\'t fully name? What were the measurements given to it?',
    },
    layer3: {
      amanahTemplate: 'Your recognition is a form of worship. Every observation documented is a form of shukr. Your Amanah is to keep seeing — and to name what you see.',
    },
  },
  {
    id: 'community-caretaker',
    label: 'Community Caretaker',
    arabic: 'خَادِم المُجْتَمَع',
    icon: '⬡',
    color: '#2d7a47',
    zahir: 'Serving your community, masjid, or neighborhood',
    layer0: {
      entryStatements: [
        'Serving your community, masjid, or neighborhood',
        'Connected to local resources and organizations',
        'Seeking to address needs in underserved areas',
      ],
      entryPrompts: [
        'What need in your community is going unmet?',
        'Who in your neighborhood is hardest to reach?',
        'What does Islah look like on your block?',
      ],
      primaryModule: { title: 'Muslim Resource Guide', href: '/muslim-resource-guide' },
    },
    layer1: {
      modules: [
        { icon: '⬡', title: 'Muslim Resource Guide', description: 'City-by-city resources, caretaker roles, community networks', href: '/muslim-resource-guide' },
        { icon: '⌘', title: 'Justice Resources', description: 'Justice-involved support and advocacy tools', href: '/muslim-resource-guide' },
        { icon: '◈', title: 'Keys (AMANAH + MIZAN)', description: 'The trust of witnessing and the balance of justice', href: '/keys-to-kulli-haal' },
        { icon: '⬡', title: 'Community', description: 'Connect with the caretaker network', href: '/community' },
        { icon: '◎', title: 'IRL Lens (community tiles)', description: 'Observe your community through the recognition arc', href: '/inner-reality-lens' },
      ],
      sequence: 'Mycorrhizal Network (Al-Jami — the Gatherer)',
      keysConnection: 'Key 05: AMANAH — The trust of those in your care',
    },
    layer2: {
      deepConnections: [
        'Mizan — the balance being violated in your community right now',
        'Amanah — the trust of witnessing injustice and not looking away',
        'As-Salam — the peace being sought beneath every community need',
      ],
      namesLink: { names: ['Al-Adl', 'As-Salam', 'Al-Jami'], href: '/names-reference' },
      openLetterSection: 'Trip, Haqq-Truth-Reality, Branding-Closing',
      reflection: 'What rights of creation are being transgressed in your community? What does the Mizan look like on your street?',
    },
    layer3: {
      amanahTemplate: 'You are entrusted with witnessing. Your documentation is advocacy. Your presence is Islah in action. Your Amanah is to stay — and to bring others with you.',
    },
  },
  {
    id: 'explorer',
    label: 'Casual Explorer',
    arabic: 'مُسَافِر',
    icon: '✦',
    color: '#c9a84c',
    zahir: 'Curious, browsing, open to discovery',
    layer0: {
      entryStatements: [
        'Curious, browsing, open to discovery',
        'Interested in wholesome, meaningful content',
        'Looking for something different',
      ],
      entryPrompts: [
        'What are you curious about right now?',
        'What would you explore if you had the perfect tool?',
        'What does a meaningful journey look like to you?',
      ],
      primaryModule: { title: 'Guided Intro', href: '/guided-intro' },
    },
    layer1: {
      modules: [
        { icon: '✦', title: 'Guided Intro', description: 'Five minutes — discover your caretaker role', href: '/guided-intro' },
        { icon: '◎', title: 'IRL Lens', description: 'The recognition game — observe, question, connect, cross', href: '/inner-reality-lens' },
        { icon: '🌿', title: 'Ayat in Creation', description: 'Immersive signs in the natural world', href: '/ayat-in-creation' },
        { icon: '✦', title: 'Names Reference', description: 'Beautiful Names — the vocabulary of reality', href: '/names-reference' },
        { icon: '✦', title: 'Open Letter', description: 'The foundation document — start anywhere', href: '/open-letter' },
      ],
      sequence: 'Any — let curiosity lead',
      keysConnection: 'Key 02: HAAL — Every state is a doorway',
    },
    layer2: {
      deepConnections: [
        'Fitra — your original design calling you back to recognition',
        'Haal — every state you\'re in is a doorway to something deeper',
        'Ayah — every sign you notice is an invitation from Al-Khabir',
      ],
      namesLink: { names: ['Al-Latif', 'Al-Khabir', 'Al-Wajid'], href: '/names-reference' },
      openLetterSection: 'Recognition opening, Rope of Language, Environmental Design',
      reflection: 'What surprised you today? What did you notice that you\'d usually walk past?',
    },
    layer3: {
      amanahTemplate: 'Curiosity is a form of gratitude. Your exploration is a form of worship. Your Amanah is to follow the thread — wherever it leads.',
    },
  },
  {
    id: 'academic',
    label: 'Academic / Researcher',
    arabic: 'بَاحِث',
    icon: '◈',
    color: '#d4a82a',
    zahir: 'Researching Islamic sciences, ecology, or social systems',
    layer0: {
      entryStatements: [
        'Researching Islamic sciences, ecology, or social systems',
        'Academic curiosity about the framework\'s methodology',
        'Looking for rigorous conceptual grounding',
      ],
      entryPrompts: [
        'What research question brought you here?',
        'What methodology are you looking for?',
        'How does sacred language relate to your field?',
      ],
      primaryModule: { title: 'Names Reference', href: '/names-reference' },
    },
    layer1: {
      modules: [
        { icon: '✦', title: 'Names Reference', description: 'Ontological vocabulary — Names as the structure of reality', href: '/names-reference' },
        { icon: '◈', title: 'SL Glossary', description: 'Etymology, Quranic roots, cross-links — the scholarly apparatus', href: '/sl-glossary' },
        { icon: '✦', title: 'Open Letter (full)', description: '32-section annotated foundation document', href: '/open-letter' },
        { icon: '⌘', title: 'Keys (all 7)', description: 'Seven conceptual frameworks — Ayat through Yaqeen', href: '/keys-to-kulli-haal' },
        { icon: '🌿', title: 'Ayat in Creation', description: 'Ecosystems as Quranic commentary — the methodology in nature', href: '/ayat-in-creation' },
      ],
      sequence: 'Leaf Sequence + Great Oxygenation',
      keysConnection: 'Key 07: YAQEEN — Certainty through evidence and methodology',
    },
    layer2: {
      deepConnections: [
        'Huduth Dhati/Zamani — the framework\'s ontological structure for contingent existence',
        'Mahiyya — essence as a research category, what makes a thing what it is',
        'Furqan — the Quran as the ultimate discernment methodology',
      ],
      namesLink: { names: ['Al-Alim', 'Al-Khabir', 'Al-Hakim'], href: '/names-reference' },
      openLetterSection: 'Trunk-Fahm, Al-Khabir·Al-Zahir, Wujud·Huduth, Re-Introduction',
      reflection: 'What does the framework\'s approach to Huduth Dhati offer that existing methodologies don\'t?',
    },
    layer3: {
      amanahTemplate: 'Knowledge that doesn\'t lead to action is a trust unfulfilled. Your scholarship is a form of Khalifa work. Your Amanah is to make the knowledge accessible — and to act on what you find.',
    },
  },
  {
    id: 'activist',
    label: 'Activist / Organizer',
    arabic: 'مُنَاضِل',
    icon: '⌘',
    color: '#2d8a87',
    zahir: 'Addressing systemic injustice, redlining, environmental racism',
    layer0: {
      entryStatements: [
        'Addressing systemic injustice, redlining, environmental racism',
        'Organizing communities around rights and resources',
        'Seeking frameworks that root justice in deeper purpose',
      ],
      entryPrompts: [
        'What injustice are you currently addressing?',
        'What would a Mizan-based framework change about your work?',
        'Who are you building power with?',
      ],
      primaryModule: { title: 'MRG Justice Resources', href: '/muslim-resource-guide' },
    },
    layer1: {
      modules: [
        { icon: '⬡', title: 'MRG (Justice filter)', description: 'Justice-involved resources, city-by-city mapping', href: '/muslim-resource-guide' },
        { icon: '⌘', title: 'Keys (MIZAN + AMANAH)', description: 'The balance and the trust — the two pillars of justice work', href: '/keys-to-kulli-haal' },
        { icon: '◎', title: 'IRL Lens (Mizan visualization)', description: 'Observe injustice through the recognition arc', href: '/inner-reality-lens' },
        { icon: '✦', title: 'Open Letter (Haqq, Steppin, Trip)', description: 'The sections most relevant to justice organizing', href: '/open-letter' },
        { icon: '⬡', title: 'Phase 2 MRG Beta', description: 'Deeper community mapping and advocacy tools', href: '/muslim-resource-guide' },
      ],
      sequence: 'Great Oxygenation (cycles of transformation) + Salmon Run (upstream work)',
      keysConnection: 'Key 04: MIZAN — The balance as a cosmic principle, not a political position',
    },
    layer2: {
      deepConnections: [
        'Al-Adl — The Just — the Name that grounds all justice work in divine reality',
        'Al-Haqq — reality and rights as one concept, inseparable in the Arabic root',
        'Islah — the root of all reform, sharing its root with Salah (prayer)',
      ],
      namesLink: { names: ['Al-Adl', 'Al-Haqq', 'Al-Muqsit'], href: '/names-reference' },
      openLetterSection: 'Haqq-Truth-Reality, Steppin, Trip, Branding-Closing',
      reflection: 'What is the Haqq being transgressed? What does the Mizan look like if restored?',
    },
    layer3: {
      amanahTemplate: 'You are standing for what was always true. Your work is Islah — mending what was torn. Your Amanah is to keep the Mizan in view — and to name the Haqq clearly.',
    },
  },
  {
    id: 'local-resource',
    label: 'Local Business / Resource',
    arabic: 'مَوْرِد مَحَلِّي',
    icon: '◉',
    color: '#5a5a8a',
    zahir: 'A local business, masjid, clinic, school, or service provider',
    layer0: {
      entryStatements: [
        'A local business, masjid, clinic, school, or service provider',
        'Looking to make services accessible to the community',
        'Interested in connecting with local networks',
      ],
      entryPrompts: [
        'What service do you provide?',
        'Who in your community can\'t reach you?',
        'What would it mean to be listed as a caretaker resource?',
      ],
      primaryModule: { title: 'MRG (submit a resource)', href: '/muslim-resource-guide' },
    },
    layer1: {
      modules: [
        { icon: '⬡', title: 'Muslim Resource Guide', description: 'Get listed — make your services accessible to your locality', href: '/muslim-resource-guide' },
        { icon: '⬡', title: 'Community', description: 'Connect with the caretaker network', href: '/community' },
        { icon: '⌘', title: 'Keys (AMANAH)', description: 'The trust of having resources others need', href: '/keys-to-kulli-haal' },
        { icon: '◎', title: 'IRL Lens (ward view)', description: 'See your city through the recognition arc', href: '/inner-reality-lens' },
        { icon: '⬡', title: 'Phase 2 MRG Beta', description: 'Advanced listing and community connection tools', href: '/muslim-resource-guide' },
      ],
      sequence: 'Mycorrhizal Network (the underground network of provision)',
      keysConnection: 'Key 05: AMANAH — The trust of having resources others need',
    },
    layer2: {
      deepConnections: [
        'Ar-Razzaq — your provision is a form of risq passing through you to others',
        'Khalifa — your business is a caretaking role, not just a commercial one',
        'Amanah — the trust of having resources that others in your community need',
      ],
      namesLink: { names: ['Ar-Razzaq', 'Al-Mughni', 'Al-Wahhab'], href: '/names-reference' },
      openLetterSection: 'Trip, Purposeful Reality, MRG Provision Layers',
      reflection: 'What would it mean to frame your business as a manifestation of Ar-Razzaq? What risq passes through you to others?',
    },
    layer3: {
      amanahTemplate: 'Your provision is not yours to keep. It passes through you. Your service is worship. Your Amanah is to make the connection — between what you have and who needs it.',
    },
  },
];

const INTEREST_DIMENSIONS = [
  { id: 'inner-reality', label: 'Inner Reality', icon: '◎', desc: 'Names, Batin, contemplation', profiles: ['inner-seeker', 'academic'] },
  { id: 'community-service', label: 'Community Service', icon: '⬡', desc: 'Resources, networks, care', profiles: ['community-caretaker', 'activist', 'local-resource'] },
  { id: 'exploration', label: 'Exploration', icon: '✦', desc: 'Ayat, immersion, discovery', profiles: ['explorer', 'inner-seeker'] },
  { id: 'scholarship', label: 'Scholarship', icon: '◈', desc: 'Research, etymology, framework', profiles: ['academic', 'inner-seeker'] },
  { id: 'justice', label: 'Justice & Reform', icon: '⌘', desc: 'Mizan, Islah, organizing', profiles: ['activist', 'community-caretaker'] },
  { id: 'provision', label: 'Provision & Business', icon: '◉', desc: 'Rizq, Falah, local resources', profiles: ['local-resource', 'community-caretaker'] },
];

const LAYER_CONFIG = [
  { key: 'zahir', label: 'ZAHIR · الظاهر', sublabel: 'Observable Entry Point', color: '#2d8a87', accent: 'rgba(45,138,135,0.12)', border: 'rgba(45,138,135,0.3)' },
  { key: 'sunnat', label: 'SUNNAT · السنة', sublabel: 'The Way that fits your path', color: '#2d7a47', accent: 'rgba(45,122,71,0.12)', border: 'rgba(45,122,71,0.3)' },
  { key: 'batin', label: 'BATIN · الباطن', sublabel: 'The hidden connections', color: '#c9a84c', accent: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.3)' },
  { key: 'reshat', label: 'RESHAT AL-TAQWAH · رشأ التقوى', sublabel: 'Your garment is taking shape', color: '#d4a82a', accent: 'rgba(212,168,42,0.08)', border: 'rgba(212,168,42,0.4)' },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function ModularCaretakerAssembly() {
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [expandedProfile, setExpandedProfile] = useState<string | null>(null);
  const [savedRole, setSavedRole] = useState<string | null>(null);
  const [depthLayers, setDepthLayers] = useState<Record<string, 0 | 1 | 2 | 3>>({});

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const role = localStorage.getItem('haal_role');
      setSavedRole(role);
      if (role) {
        const profile = CARETAKER_PROFILES.find(p => p.id === role);
        if (profile) setExpandedProfile(profile.id);
      }
    }
  }, []);

  const toggleInterest = (id: string) => {
    setSelectedInterests(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const matchedProfiles = selectedInterests.length === 0
    ? CARETAKER_PROFILES
    : CARETAKER_PROFILES.filter(p =>
        selectedInterests.some(interest => {
          const dim = INTEREST_DIMENSIONS.find(d => d.id === interest);
          return dim?.profiles.includes(p.id);
        })
      );

  const handleProfileExpand = (profileId: string) => {
    setExpandedProfile(prev => prev === profileId ? null : profileId);
    if (!depthLayers[profileId]) {
      setDepthLayers(prev => ({ ...prev, [profileId]: 0 }));
    }
  };

  const advanceLayer = (profileId: string, currentLayer: 0 | 1 | 2 | 3) => {
    const next = Math.min(currentLayer + 1, 3) as 0 | 1 | 2 | 3;
    setDepthLayers(prev => ({ ...prev, [profileId]: next }));
  };

  const handleRoleSelect = async (profile: CaretakerProfile) => {
    if (typeof window !== 'undefined') localStorage.setItem('haal_role', profile.id);
    setSavedRole(profile.id);
    await trackResonance('caretaker_profile', profile.id, profile.label, 'role_select');
    await updateUserProfile(profile.id, selectedInterests);
    advanceLayer(profile.id, depthLayers[profile.id] ?? 0);
  };

  return (
    <section className="py-24 px-6 relative overflow-hidden" style={{ background: '#060d12' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(45,122,71,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(45,122,71,0.02) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="max-w-[1280px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>
            ✦ Modular Caretaker Assembly
          </p>
          <h2 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
            Who Are You Here As?
          </h2>
          <p className="font-mono text-sm max-w-2xl mx-auto mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Every caretaker arrives with a different haal — state, circumstance, need. Select what resonates. The platform assembles itself around you.
          </p>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(201,168,76,0.5)', direction: 'rtl' }}>
            كُلُّ نَفْسٍ بِمَا كَسَبَتْ رَهِينَةٌ
          </p>
          <p className="font-mono text-[10px] italic mt-1" style={{ color: 'rgba(212,197,169,0.3)' }}>
            Every soul is held in pledge for what it has earned — (74:38)
          </p>
        </div>

        {/* Interest Selector */}
        <div className="mb-10">
          <p className="font-mono text-[10px] uppercase tracking-widest mb-4 text-center" style={{ color: 'rgba(212,197,169,0.4)' }}>
            Select your interests — or explore all profiles below
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {INTEREST_DIMENSIONS.map(dim => {
              const active = selectedInterests.includes(dim.id);
              return (
                <button
                  key={dim.id}
                  onClick={() => toggleInterest(dim.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200"
                  style={{
                    background: active ? 'rgba(201,168,76,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${active ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)'}`,
                    color: active ? '#c9a84c' : 'rgba(212,197,169,0.5)',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{dim.icon}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest">{dim.label}</span>
                </button>
              );
            })}
            {selectedInterests.length > 0 && (
              <button
                onClick={() => setSelectedInterests([])}
                className="px-3 py-2 rounded-full font-mono text-[9px] uppercase tracking-widest transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(212,197,169,0.3)' }}
              >
                Clear ×
              </button>
            )}
          </div>
        </div>

        {/* Profile Cards */}
        <div className="space-y-4">
          {matchedProfiles.map(profile => {
            const isExpanded = expandedProfile === profile.id;
            const currentLayer = depthLayers[profile.id] ?? 0;
            return (
              <ProfileCard
                key={profile.id}
                profile={profile}
                isExpanded={isExpanded}
                isSaved={savedRole === profile.id}
                currentLayer={currentLayer}
                onToggle={() => handleProfileExpand(profile.id)}
                onRoleSelect={() => handleRoleSelect(profile)}
                onAdvanceLayer={() => advanceLayer(profile.id, currentLayer)}
              />
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.25)' }}>
            The zahir elements are the starting place. The batin elements are suggested routes.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.2)', fontSize: 12, fontStyle: 'italic' }}>
            A person may not even know they had these interests buried in them, waiting to be called on.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── PROFILE CARD ─────────────────────────────────────────────────────────────

function ProfileCard({
  profile, isExpanded, isSaved, currentLayer, onToggle, onRoleSelect, onAdvanceLayer,
}: {
  profile: CaretakerProfile;
  isExpanded: boolean;
  isSaved: boolean;
  currentLayer: 0 | 1 | 2 | 3;
  onToggle: () => void;
  onRoleSelect: () => void;
  onAdvanceLayer: () => void;
}) {
  const [resonanceSummary, setResonanceSummary] = useState<ResonanceSummary | null>(null);
  const [loadingReshat, setLoadingReshat] = useState(false);

  const loadReshat = useCallback(async () => {
    if (currentLayer === 3 && !resonanceSummary) {
      setLoadingReshat(true);
      const summary = await getUserResonanceSummary();
      setResonanceSummary(summary);
      setLoadingReshat(false);
    }
  }, [currentLayer, resonanceSummary]);

  useEffect(() => {
    if (isExpanded && currentLayer === 3) loadReshat();
  }, [isExpanded, currentLayer, loadReshat]);

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        border: `1px solid ${isExpanded ? profile.color + '35' : 'rgba(255,255,255,0.06)'}`,
        borderTop: `3px solid ${isExpanded ? profile.color : profile.color + '40'}`,
        background: isExpanded ? `${profile.color}04` : 'rgba(255,255,255,0.01)',
      }}
    >
      {/* Card Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full text-left p-5 flex items-center justify-between group"
      >
        <div className="flex items-center gap-3">
          <span style={{ fontSize: 24, color: profile.color }}>{profile.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{profile.label}</h3>
              {isSaved && (
                <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${profile.color}12`, border: `1px solid ${profile.color}30`, color: profile.color }}>
                  Your Role
                </span>
              )}
            </div>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${profile.color}70`, direction: 'rtl' }}>{profile.arabic}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isExpanded && (
            <div className="hidden sm:flex items-center gap-1">
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full transition-all duration-300"
                  style={{
                    background: i <= currentLayer ? LAYER_CONFIG[i].color : 'rgba(255,255,255,0.1)',
                    boxShadow: i === currentLayer ? `0 0 6px ${LAYER_CONFIG[i].color}` : 'none',
                  }}
                />
              ))}
            </div>
          )}
          <span
            className="font-mono text-[9px] uppercase tracking-widest transition-all duration-200"
            style={{ color: isExpanded ? profile.color : 'rgba(212,197,169,0.3)' }}
          >
            {isExpanded ? 'Fold ↑' : 'Unfold ↓'}
          </span>
        </div>
      </button>

      {/* Expandable Depth Layers */}
      <div
        className="overflow-hidden transition-all duration-400"
        style={{
          maxHeight: isExpanded ? '3000px' : '0',
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? 'translateY(0)' : 'translateY(8px)',
        }}
      >
        {/* Layer 0 — ZAHIR */}
        <Layer0
          profile={profile}
          onResonate={onRoleSelect}
          isSaved={isSaved}
        />

        {/* Layer 1 — SUNNAT */}
        {currentLayer >= 1 && (
          <LayerReveal>
            <Layer1
              profile={profile}
              onAdvance={onAdvanceLayer}
            />
          </LayerReveal>
        )}

        {/* Layer 2 — BATIN */}
        {currentLayer >= 2 && (
          <LayerReveal>
            <Layer2
              profile={profile}
              onAdvance={onAdvanceLayer}
            />
          </LayerReveal>
        )}

        {/* Layer 3 — RESHAT AL-TAQWAH */}
        {currentLayer >= 3 && (
          <LayerReveal>
            <Layer3
              profile={profile}
              resonanceSummary={resonanceSummary}
              loading={loadingReshat}
            />
          </LayerReveal>
        )}
      </div>
    </div>
  );
}

function LayerReveal({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className="transition-all duration-400"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {children}
    </div>
  );
}

// ─── LAYER 0 — ZAHIR ─────────────────────────────────────────────────────────

function Layer0({ profile, onResonate, isSaved }: { profile: CaretakerProfile; onResonate: () => void; isSaved: boolean }) {
  const cfg = LAYER_CONFIG[0];
  return (
    <div className="mx-5 mb-4 rounded-lg overflow-hidden" style={{ background: cfg.accent, border: `1px solid ${cfg.border}` }}>
      <div className="px-5 pt-4 pb-2 flex items-center gap-2 border-b" style={{ borderColor: cfg.border }}>
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
        <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.35)' }}>— {cfg.sublabel}</span>
      </div>
      <div className="p-5">
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>What brings you here?</p>
          <div className="space-y-2">
            {profile.layer0.entryStatements.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span style={{ color: cfg.color, fontSize: 10, marginTop: 2 }}>◦</span>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.6 }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-5">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Entry prompts</p>
          <div className="space-y-1.5">
            {profile.layer0.entryPrompts.map((q, i) => (
              <p key={i} style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, fontStyle: 'italic' }}>"{q}"</p>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.35)' }}>Start with:</span>
            <Link
              href={profile.layer0.primaryModule.href}
              className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
              style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, color: cfg.color }}
            >
              {profile.layer0.primaryModule.title} →
            </Link>
          </div>
          <button
            onClick={onResonate}
            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 rounded transition-all duration-200"
            style={{
              background: isSaved ? `${cfg.color}20` : `${cfg.color}10`,
              border: `1px solid ${cfg.color}`,
              color: cfg.color,
            }}
          >
            {isSaved ? '✓ This is my path' : 'This resonates →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LAYER 1 — SUNNAT ────────────────────────────────────────────────────────

function Layer1({ profile, onAdvance }: { profile: CaretakerProfile; onAdvance: () => void }) {
  const cfg = LAYER_CONFIG[1];
  return (
    <div className="mx-5 mb-4 rounded-lg overflow-hidden" style={{ background: cfg.accent, border: `1px solid ${cfg.border}` }}>
      <div className="px-5 pt-4 pb-2 flex items-center gap-2 border-b" style={{ borderColor: cfg.border }}>
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
        <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.35)' }}>— {cfg.sublabel}</span>
      </div>
      <div className="p-5">
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>Recommended modules — in order</p>
          <div className="space-y-2">
            {profile.layer1.modules.map((m, i) => (
              <Link
                key={i}
                href={m.href}
                className="flex items-center gap-3 rounded-lg p-3 group transition-all"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = `${cfg.color}30`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.05)'; }}
              >
                <span className="font-mono text-xs w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: `${cfg.color}15`, color: cfg.color, fontSize: 10 }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-xs font-bold uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{m.title}</span>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11 }}>{m.description}</p>
                </div>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: cfg.color, fontSize: 12 }}>→</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <div className="rounded p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: cfg.color }}>Sequence</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>{profile.layer1.sequence}</p>
          </div>
          <div className="rounded p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: cfg.color }}>Keys Connection</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>{profile.layer1.keysConnection}</p>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onAdvance}
            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 rounded transition-all duration-200"
            style={{ background: `${cfg.color}10`, border: `1px solid ${cfg.color}`, color: cfg.color }}
          >
            Begin this path →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LAYER 2 — BATIN ─────────────────────────────────────────────────────────

function Layer2({ profile, onAdvance }: { profile: CaretakerProfile; onAdvance: () => void }) {
  const cfg = LAYER_CONFIG[2];
  return (
    <div className="mx-5 mb-4 rounded-lg overflow-hidden" style={{ background: cfg.accent, border: `1px solid ${cfg.border}` }}>
      <div className="px-5 pt-4 pb-2 flex items-center gap-2 border-b" style={{ borderColor: cfg.border }}>
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
        <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.35)' }}>— {cfg.sublabel}</span>
      </div>
      <div className="p-5">
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>Deeper framework connections</p>
          <div className="space-y-2">
            {profile.layer2.deepConnections.map((c, i) => (
              <div key={i} className="flex items-start gap-2 rounded p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ color: cfg.color, fontSize: 12, marginTop: 1 }}>◈</span>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.6 }}>{c}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Link
            href={profile.layer2.namesLink.href}
            className="rounded p-3 transition-all group"
            style={{ background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.2)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(138,138,184,0.4)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(138,138,184,0.2)'; }}
          >
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: '#8a8ab8' }}>Most Active Names →</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>{profile.layer2.namesLink.names.join(' · ')}</p>
          </Link>
          <div className="rounded p-3" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: '#c9a84c' }}>Open Letter Section</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>{profile.layer2.openLetterSection}</p>
          </div>
        </div>
        <div className="rounded p-4 mb-5" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Reflection</p>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>"{profile.layer2.reflection}"</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onAdvance}
            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 rounded transition-all duration-200"
            style={{ background: `${cfg.color}10`, border: `1px solid ${cfg.color}`, color: cfg.color }}
          >
            Go deeper →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LAYER 3 — RESHAT AL-TAQWAH ──────────────────────────────────────────────

function Layer3({ profile, resonanceSummary, loading }: { profile: CaretakerProfile; resonanceSummary: ResonanceSummary | null; loading: boolean }) {
  const cfg = LAYER_CONFIG[3];
  const role = resonanceSummary?.role || profile.id;
  const topContent = resonanceSummary?.topContentTypes.slice(0, 2).join(' and ') || 'the framework';
  const totalInteractions = resonanceSummary?.totalInteractions || 0;

  const amanahPrompt = `Given your role as ${profile.label} and your resonance with ${topContent}: ${profile.layer3.amanahTemplate}`;

  return (
    <div className="mx-5 mb-5 rounded-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(212,168,42,0.06) 0%, rgba(201,168,76,0.04) 100%)', border: `1px solid ${cfg.border}` }}>
      <div className="px-5 pt-4 pb-2 flex items-center gap-2 border-b" style={{ borderColor: cfg.border }}>
        <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: cfg.color }} />
        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
        <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.35)' }}>— {cfg.sublabel}</span>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 rounded animate-pulse" style={{ background: 'rgba(212,168,42,0.08)', width: `${60 + i * 10}%` }} />
            ))}
          </div>
        ) : (
          <>
            {/* Framework map summary */}
            {totalInteractions > 0 && (
              <div className="mb-4 rounded p-4" style={{ background: 'rgba(212,168,42,0.06)', border: '1px solid rgba(212,168,42,0.15)' }}>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: cfg.color }}>Your Framework Map</p>
                <div className="flex flex-wrap gap-3">
                  <div className="text-center">
                    <div className="font-mono text-lg font-black" style={{ color: cfg.color }}>{totalInteractions}</div>
                    <div className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Interactions</div>
                  </div>
                  {resonanceSummary?.topNames.length ? (
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.4)' }}>Names Resonating</p>
                      <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>{resonanceSummary.topNames.slice(0, 3).join(' · ')}</p>
                    </div>
                  ) : null}
                  {resonanceSummary?.topKeys.length ? (
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.4)' }}>Keys Explored</p>
                      <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>{resonanceSummary.topKeys.slice(0, 3).join(' · ')}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Threads */}
            <div className="mb-4">
              <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.4)' }}>Your threads through the platform</p>
              <div className="space-y-2">
                {[
                  { label: 'Recognition Thread', desc: `${profile.label} → ${profile.layer0.primaryModule.title} → Names Reference`, color: '#c9a84c' },
                  { label: 'Connection Thread', desc: `${profile.layer2.deepConnections[0].split(' — ')[0]} → Open Letter → Keys`, color: '#2d8a87' },
                  { label: 'Action Thread', desc: `Framework Map → ${profile.layer1.sequence.split(' (')[0]} → Community`, color: '#2d7a47' },
                ].map((thread, i) => (
                  <div key={i} className="flex items-start gap-2 rounded p-2.5" style={{ background: 'rgba(255,255,255,0.02)', borderLeft: `2px solid ${thread.color}` }}>
                    <div className="flex-1">
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: thread.color }}>{thread.label}</p>
                      <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11 }}>{thread.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amanah prompt */}
            <div className="rounded-lg p-5 mb-5" style={{ background: 'rgba(212,168,42,0.06)', border: '1px solid rgba(212,168,42,0.2)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: cfg.color }}>Your Amanah</p>
              <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 13, lineHeight: 1.8, fontStyle: 'italic' }}>{amanahPrompt}</p>
            </div>

            {/* Closing */}
            <div className="text-center mb-4">
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#c9a84c', direction: 'rtl', marginBottom: 4 }}>الحمد لله</p>
              <p className="font-mono text-[10px] italic" style={{ color: 'rgba(212,197,169,0.4)' }}>
                Alhamdulilah — your garment grows with every recognition
              </p>
            </div>

            <div className="flex justify-center">
              <Link
                href="/user-dashboard"
                className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 rounded transition-all duration-200"
                style={{ background: 'rgba(212,168,42,0.12)', border: `1px solid ${cfg.color}`, color: cfg.color }}
              >
                View your full framework map →
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
